import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle,
    Calendar as CalendarIcon,
    Check,
    ChevronLeft,
    ChevronRight,
    Loader2,
    MapPin,
    Navigation,
    Phone,
    Plus,
    SkipForward,
    Trash2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const isoDay = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
const addDays = (d, n) => {
    const c = new Date(d);
    c.setDate(c.getDate() + n);
    return c;
};
const fmtHeader = (d) => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const yesterday = addDays(today, -1);
    if (isoDay(d) === isoDay(today)) return "Today";
    if (isoDay(d) === isoDay(tomorrow)) return "Tomorrow";
    if (isoDay(d) === isoDay(yesterday)) return "Yesterday";
    return d.toLocaleDateString("en-GB", { weekday: "long" });
};
const fmtDate = (d) =>
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const fmtGBP = (pence) =>
    pence ? `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : null;
const mapsHref = (address) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || "")}`;

// -------------------------------------------------------- Swipeable job card
const SwipeableJobCard = ({ appt, onDone, onSkip, onOpen }) => {
    const price = fmtGBP(appt.service_price_pence);

    return (
        <motion.div
            className="relative select-none touch-pan-y"
            data-testid={`field-card-${appt.id}`}
        >
            {/* Green Done background */}
            <div className="absolute inset-0 rounded-none bg-emerald-500 flex items-center justify-start px-6 text-white font-black uppercase tracking-widest text-lg">
                <Check size={22} className="mr-2" /> Done
            </div>
            {/* Red Skip background */}
            <div className="absolute inset-0 rounded-none bg-orange-500 flex items-center justify-end px-6 text-white font-black uppercase tracking-widest text-lg">
                Skip <SkipForward size={22} className="ml-2" />
            </div>

            <motion.div
                className="relative bg-white border-2 border-neutral-900 p-4 cursor-pointer active:bg-neutral-50"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.25}
                onDragEnd={(_, info) => {
                    if (info.offset.x > 120) onDone(appt);
                    else if (info.offset.x < -120) onSkip(appt);
                }}
                onClick={() => onOpen(appt)}
                whileTap={{ scale: 0.99 }}
                data-testid={`field-swipe-${appt.id}`}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-widest font-black text-orange-600">
                            {appt.appointment_time_tbc ? "Time TBC" : appt.appointment_time || ""}
                            {appt.needs_rebook && (
                                <span className="ml-2 text-red-600" data-testid={`field-rebook-flag-${appt.id}`}>
                                    · Needs rebook
                                </span>
                            )}
                        </p>
                        <p className="text-lg font-black leading-tight mt-0.5 truncate" data-testid={`field-name-${appt.id}`}>
                            {appt.customer_name}
                        </p>
                        <a
                            href={mapsHref(appt.property_address)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-start gap-1 text-sm text-neutral-600 mt-1 underline"
                            data-testid={`field-address-${appt.id}`}
                        >
                            <MapPin size={14} className="shrink-0 mt-0.5" />
                            <span className="line-clamp-2">{appt.property_address}</span>
                        </a>
                        <p className="text-sm text-neutral-700 mt-2 font-medium">
                            {appt.service_name}
                            {price && (
                                <span className="ml-2 text-neutral-900 font-black" data-testid={`field-price-${appt.id}`}>
                                    · {price}
                                </span>
                            )}
                        </p>
                    </div>
                    {appt.customer_mobile && (
                        <a
                            href={`tel:${appt.customer_mobile.replace(/\s/g, "")}`}
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0 w-11 h-11 bg-emerald-500 border-2 border-neutral-900 flex items-center justify-center text-white"
                            data-testid={`field-call-${appt.id}`}
                        >
                            <Phone size={20} />
                        </a>
                    )}
                </div>
                <p className="text-[10px] text-neutral-400 mt-3 uppercase tracking-wider">
                    Swipe right = Done · Left = Skip · Tap for options
                </p>
            </motion.div>
        </motion.div>
    );
};

// -------------------------------------------------------- Mini month picker
const MonthPicker = ({ open, onClose, onPick, cursor, setCursor, appointmentsByDay }) => {
    if (!open) return null;
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startWeekday = (monthStart.getDay() + 6) % 7; // Monday first
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let i = 1; i <= daysInMonth; i++)
        cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    const today = new Date();

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="bg-white border-2 border-neutral-900 rounded-none max-w-sm"
                data-testid="field-month-picker"
            >
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <button
                            className="w-9 h-9 border-2 border-neutral-900 flex items-center justify-center"
                            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                            data-testid="field-month-prev"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <DialogTitle className="font-display uppercase" data-testid="field-month-title">
                            {cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                        </DialogTitle>
                        <button
                            className="w-9 h-9 border-2 border-neutral-900 flex items-center justify-center"
                            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                            data-testid="field-month-next"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </DialogHeader>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-neutral-500">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                        <div key={i}>{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1 mt-1">
                    {cells.map((c, i) => {
                        if (!c) return <div key={i} />;
                        const key = isoDay(c);
                        const count = appointmentsByDay[key] || 0;
                        const isToday = isoDay(c) === isoDay(today);
                        return (
                            <button
                                key={i}
                                onClick={() => onPick(c)}
                                data-testid={`field-month-day-${key}`}
                                className={`aspect-square flex flex-col items-center justify-center border-2 text-sm font-bold ${
                                    isToday
                                        ? "border-orange-500 bg-orange-50 text-orange-700"
                                        : "border-neutral-200"
                                } ${count > 0 ? "font-black" : ""}`}
                            >
                                <span>{c.getDate()}</span>
                                {count > 0 && (
                                    <span className="text-[9px] font-black text-orange-600 -mt-1">
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// -------------------------------------------------------- Job options modal
const JobModal = ({ appt, onClose, onDone, onSkip, onDelete, onAddPart, onDeletePart }) => {
    const [partName, setPartName] = useState("");
    const [partQty, setPartQty] = useState("1");
    const [partPrice, setPartPrice] = useState("");
    const [savingPart, setSavingPart] = useState(false);

    if (!appt) return null;

    const submitPart = async (e) => {
        e.preventDefault();
        const qty = parseFloat(partQty) || 1;
        const price = parseFloat(partPrice);
        if (!partName.trim()) return toast.error("Part name");
        if (!price || price < 0) return toast.error("Enter a price");
        setSavingPart(true);
        try {
            await onAddPart(appt, {
                name: partName.trim(),
                quantity: qty,
                unit_price_gbp: price,
            });
            setPartName("");
            setPartQty("1");
            setPartPrice("");
        } finally {
            setSavingPart(false);
        }
    };

    const parts = appt.parts || [];
    const partsTotal = parts.reduce((n, p) => n + (p.total_pence || 0), 0);

    return (
        <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-white border-2 border-neutral-900 rounded-none max-h-[90vh] overflow-y-auto" data-testid="field-job-modal">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase">{appt.customer_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 text-sm">
                    <a
                        href={mapsHref(appt.property_address)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-start gap-2 underline"
                    >
                        <MapPin size={16} className="shrink-0 mt-0.5" />
                        <span>{appt.property_address}</span>
                    </a>
                    {appt.customer_mobile && (
                        <a
                            href={`tel:${appt.customer_mobile.replace(/\s/g, "")}`}
                            className="flex items-center gap-2 underline"
                        >
                            <Phone size={16} /> {appt.customer_mobile}
                        </a>
                    )}
                    <p className="mt-2">
                        <span className="font-bold">{appt.service_name}</span>
                        {fmtGBP(appt.service_price_pence) && (
                            <span className="ml-2 text-orange-600 font-black">
                                {fmtGBP(appt.service_price_pence)}
                            </span>
                        )}
                    </p>
                    {appt.notes && (
                        <p className="text-neutral-600 whitespace-pre-line mt-2">{appt.notes}</p>
                    )}
                </div>

                {/* Parts / materials */}
                <div className="mt-4 border-t-2 border-neutral-900 pt-3">
                    <p className="text-[10px] uppercase tracking-widest font-black text-neutral-500 mb-2">
                        Parts &amp; materials
                        {parts.length > 0 && (
                            <span className="text-neutral-800 ml-2">
                                · total {fmtGBP(partsTotal)}
                            </span>
                        )}
                    </p>
                    <div className="space-y-1" data-testid="field-parts-list">
                        {parts.map((p) => (
                            <div
                                key={p.id}
                                className="flex items-center gap-2 text-sm"
                                data-testid={`field-part-${p.id}`}
                            >
                                <span className="flex-1 truncate">
                                    {p.quantity !== 1 ? `${p.quantity} × ` : ""}
                                    {p.name}
                                </span>
                                <span className="font-bold">{fmtGBP(p.total_pence)}</span>
                                <button
                                    onClick={() => onDeletePart(appt, p.id)}
                                    className="text-neutral-400 hover:text-red-600"
                                    aria-label="Remove part"
                                    data-testid={`field-part-del-${p.id}`}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={submitPart} className="mt-2 grid grid-cols-[1fr_60px_80px_auto] gap-1 items-center">
                        <input
                            type="text"
                            value={partName}
                            onChange={(e) => setPartName(e.target.value)}
                            placeholder="Part name"
                            className="border-2 border-neutral-900 h-9 px-2 text-sm"
                            data-testid="field-part-name"
                        />
                        <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={partQty}
                            onChange={(e) => setPartQty(e.target.value)}
                            placeholder="Qty"
                            className="border-2 border-neutral-900 h-9 px-2 text-sm text-center"
                            data-testid="field-part-qty"
                        />
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={partPrice}
                            onChange={(e) => setPartPrice(e.target.value)}
                            placeholder="£"
                            className="border-2 border-neutral-900 h-9 px-2 text-sm"
                            data-testid="field-part-price"
                        />
                        <button
                            type="submit"
                            disabled={savingPart}
                            className="dh-btn h-9 !px-2"
                            data-testid="field-part-add"
                            aria-label="Add part"
                        >
                            <Plus size={16} />
                        </button>
                    </form>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <button
                        onClick={() => onDelete(appt)}
                        className="dh-btn w-full sm:w-auto"
                        data-variant="ghost"
                        data-testid="field-modal-delete"
                    >
                        <Trash2 size={16} /> Delete
                    </button>
                    <button
                        onClick={() => onSkip(appt)}
                        className="dh-btn w-full sm:w-auto bg-orange-500 border-orange-700 text-white"
                        data-testid="field-modal-skip"
                    >
                        <SkipForward size={16} /> Skip
                    </button>
                    <button
                        onClick={() => onDone(appt)}
                        className="dh-btn w-full sm:w-auto bg-emerald-500 border-emerald-700 text-white"
                        data-testid="field-modal-done"
                    >
                        <Check size={16} /> Done
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// -------------------------------------------------------- Field page
const Field = () => {
    const [day, setDay] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [monthOpen, setMonthOpen] = useState(false);
    const [monthCursor, setMonthCursor] = useState(new Date());
    const [selected, setSelected] = useState(null);

    // Done (invoice) dialog state
    const [doneAppt, setDoneAppt] = useState(null);
    const [doneAmount, setDoneAmount] = useState("");
    const [doneDesc, setDoneDesc] = useState("");
    const [doneSaving, setDoneSaving] = useState(false);
    // Delete confirmation dialog
    const [deleteAppt, setDeleteAppt] = useState(null);
    const [deleteSaving, setDeleteSaving] = useState(false);

    // Add-job (booking) dialog state
    const [addOpen, setAddOpen] = useState(false);
    const [addSaving, setAddSaving] = useState(false);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [customerProps, setCustomerProps] = useState([]);
    const [addMode, setAddMode] = useState("existing"); // "existing" | "new"
    const [addCustomerId, setAddCustomerId] = useState("");
    const [addPropertyId, setAddPropertyId] = useState("");
    const [addNewName, setAddNewName] = useState("");
    const [addNewEmail, setAddNewEmail] = useState("");
    const [addNewMobile, setAddNewMobile] = useState("");
    const [addNewAddress, setAddNewAddress] = useState("");
    const [addServiceId, setAddServiceId] = useState("");
    const [addPrice, setAddPrice] = useState("");
    const [addTime, setAddTime] = useState("");
    const [addTBC, setAddTBC] = useState(true);
    const [addNotes, setAddNotes] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/appointments")
            .then((r) => setAppointments(r.data))
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    // Load service types and customers once for the Add-job dialog
    useEffect(() => {
        api.get("/service-types")
            .then((r) => setServiceTypes(r.data || []))
            .catch(() => {});
        api.get("/customers")
            .then((r) => setCustomers(r.data || []))
            .catch(() => {});
    }, []);

    // Fetch properties for selected customer
    useEffect(() => {
        if (!addCustomerId) {
            setCustomerProps([]);
            setAddPropertyId("");
            return;
        }
        api.get("/properties", { params: { customer_id: addCustomerId } })
            .then((r) => {
                setCustomerProps(r.data || []);
                if ((r.data || []).length === 1) setAddPropertyId(r.data[0].id);
                else setAddPropertyId("");
            })
            .catch(() => setCustomerProps([]));
    }, [addCustomerId]);

    // Auto-fill the price from the selected service type's default
    useEffect(() => {
        if (!addServiceId) return;
        const st = serviceTypes.find((s) => s.id === addServiceId);
        if (st && (addPrice === "" || addPrice === "0")) {
            setAddPrice(String(st.price ?? ""));
        }
    }, [addServiceId, serviceTypes]);

    const dayKey = isoDay(day);
    const todaysJobs = useMemo(
        () =>
            appointments.filter(
                (a) => a.appointment_date && isoDay(new Date(a.appointment_date)) === dayKey,
            ),
        [appointments, dayKey],
    );

    const appointmentsByDay = useMemo(() => {
        const map = {};
        appointments.forEach((a) => {
            if (!a.appointment_date) return;
            const k = isoDay(new Date(a.appointment_date));
            map[k] = (map[k] || 0) + 1;
        });
        return map;
    }, [appointments]);

    // Actions
    const doDone = (appt) => {
        setSelected(null);
        setDoneAppt(appt);
        // Prefill amount from primary service + any line items on the job.
        const primary = (appt.service_price_pence || 0) / 100;
        const extras = (appt.line_items || []).reduce(
            (n, li) => n + (li.total_pence || 0) / 100,
            0,
        );
        const total = primary + extras;
        setDoneAmount(total > 0 ? total.toFixed(2) : "");
        setDoneDesc(appt.service_name || "");
    };
    const submitInvoice = async (e) => {
        e.preventDefault();
        const amt = parseFloat(doneAmount);
        if (!amt || amt <= 0) return toast.error("Enter an amount");
        setDoneSaving(true);
        try {
            // Build line items from the job so the invoice PDF shows a proper
            // breakdown. If the job has extra line_items, keep them and use
            // whatever Hayden typed as the primary row total.
            const primaryTotal =
                amt -
                (doneAppt.line_items || []).reduce(
                    (n, li) => n + (li.total_pence || 0) / 100,
                    0,
                );
            const lines = [];
            if (primaryTotal > 0.005 || (doneAppt.line_items || []).length === 0) {
                lines.push({
                    name: doneDesc.trim() || doneAppt.service_name || "Service",
                    quantity: 1,
                    unit_price_gbp: primaryTotal > 0 ? primaryTotal : amt,
                });
            }
            for (const li of doneAppt.line_items || []) {
                lines.push({
                    name: li.name,
                    quantity: li.quantity ?? 1,
                    unit_price_gbp: (li.unit_price_pence || 0) / 100,
                });
            }
            await api.post(`/jobs/${doneAppt.id}/send-invoice`, {
                line_items: lines,
                mark_job_complete: true,
            });
            toast.success("Invoice sent · job complete");
            setDoneAppt(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setDoneSaving(false);
        }
    };
    const skipComplete = async () => {
        if (!doneAppt) return;
        setDoneSaving(true);
        try {
            await api.post(`/jobs/${doneAppt.id}/complete`);
            toast.success("Marked complete");
            setDoneAppt(null);
            load();
        } catch {
            toast.error("Failed");
        } finally {
            setDoneSaving(false);
        }
    };

    const doSkip = async (appt) => {
        setSelected(null);
        try {
            await api.post(`/jobs/${appt.id}/skip`);
            toast.success("Skipped · flagged for rebook");
            load();
        } catch {
            toast.error("Failed");
        }
    };
    const doDelete = (appt) => {
        setSelected(null);
        setDeleteAppt(appt);
    };

    // Parts / materials
    const addPart = async (appt, payload) => {
        try {
            await api.post(`/jobs/${appt.id}/parts`, payload);
            toast.success("Part added");
            load();
            // Update the currently-selected job so the modal shows it immediately
            const r = await api.get("/appointments");
            const updated = r.data.find((a) => a.id === appt.id);
            if (updated) setSelected(updated);
            setAppointments(r.data);
        } catch {
            toast.error("Add part failed");
        }
    };
    const deletePart = async (appt, partId) => {
        try {
            await api.delete(`/jobs/${appt.id}/parts/${partId}`);
            const r = await api.get("/appointments");
            const updated = r.data.find((a) => a.id === appt.id);
            if (updated) setSelected(updated);
            setAppointments(r.data);
        } catch {
            toast.error("Delete failed");
        }
    };

    const openRoutePlan = () => {
        const addrs = todaysJobs
            .map((a) => a.property_address)
            .filter(Boolean);
        if (addrs.length === 0) {
            return toast.error("No jobs today to route");
        }
        // Google Maps directions: first stop = destination, rest = waypoints.
        // Origin blank → uses current device location.
        const [dest, ...rest] = [...addrs].reverse();
        const url = new URL("https://www.google.com/maps/dir/");
        url.searchParams.set("api", "1");
        url.searchParams.set("destination", dest);
        if (rest.length) {
            url.searchParams.set("waypoints", rest.reverse().join("|"));
        }
        url.searchParams.set("travelmode", "driving");
        window.open(url.toString(), "_blank", "noopener");
    };

    const confirmDelete = async () => {
        if (!deleteAppt) return;
        setDeleteSaving(true);
        try {
            await api.delete(`/jobs/${deleteAppt.id}`);
            toast.success("Job deleted");
            setDeleteAppt(null);
            load();
        } catch {
            toast.error("Delete failed");
        } finally {
            setDeleteSaving(false);
        }
    };

    const openAddJob = () => {
        setAddMode("existing");
        setAddCustomerId("");
        setAddPropertyId("");
        setAddNewName("");
        setAddNewEmail("");
        setAddNewMobile("");
        setAddNewAddress("");
        const defaultSvc =
            serviceTypes.find((s) => s.name === "Annual Boiler Service") ||
            serviceTypes[0];
        setAddServiceId(defaultSvc?.id || "");
        setAddPrice(defaultSvc ? String(defaultSvc.price ?? "") : "");
        setAddTime("");
        setAddTBC(true);
        setAddNotes("");
        setAddOpen(true);
    };

    const saveAddJob = async (e) => {
        e.preventDefault();
        if (!addServiceId) return toast.error("Pick a service");
        const priceNum = addPrice === "" ? null : parseFloat(addPrice);
        if (priceNum !== null && (Number.isNaN(priceNum) || priceNum < 0)) {
            return toast.error("Enter a valid price");
        }
        setAddSaving(true);
        try {
            const dayIso = isoDay(day);
            const appointment_date =
                addTBC || !addTime ? dayIso : `${dayIso}T${addTime}:00`;
            const payload = {
                appointment_date,
                time_tbc: addTBC || !addTime,
                service_type_id: addServiceId,
                service_price: priceNum,
                notes: addNotes.trim() || null,
            };
            if (addMode === "existing") {
                if (!addCustomerId) {
                    setAddSaving(false);
                    return toast.error("Pick a customer");
                }
                payload.customer_id = addCustomerId;
                if (addPropertyId) payload.property_id = addPropertyId;
                else if (addNewAddress.trim())
                    payload.new_property_address = addNewAddress.trim();
            } else {
                if (!addNewName.trim() || !addNewAddress.trim()) {
                    setAddSaving(false);
                    return toast.error("Name and address required");
                }
                payload.new_customer_name = addNewName.trim();
                payload.new_customer_email = addNewEmail.trim() || null;
                payload.new_customer_mobile = addNewMobile.trim() || null;
                payload.new_property_address = addNewAddress.trim();
            }
            await api.post("/jobs/quick", payload);
            toast.success("Job booked in");
            setAddOpen(false);
            // Refresh appointments AND the customer list so any newly-created
            // customer appears in the picker on the next open.
            load();
            api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setAddSaving(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] pb-24" data-testid="field-page">
            {/* Day header */}
            <div className="sticky top-0 bg-white border-b-2 border-neutral-900 z-10 px-4 py-3 flex items-center justify-between gap-2">
                <button
                    onClick={() => setDay(addDays(day, -1))}
                    className="w-11 h-11 border-2 border-neutral-900 flex items-center justify-center"
                    data-testid="field-prev-day"
                >
                    <ChevronLeft size={22} />
                </button>
                <div className="text-center flex-1 min-w-0">
                    <p
                        className="text-[10px] uppercase tracking-widest font-black text-orange-600"
                        data-testid="field-day-label"
                    >
                        {fmtHeader(day)}
                    </p>
                    <p className="font-display font-black text-xl uppercase leading-none truncate">
                        {fmtDate(day)}
                    </p>
                </div>
                <button
                    onClick={() => setDay(addDays(day, 1))}
                    className="w-11 h-11 border-2 border-neutral-900 flex items-center justify-center"
                    data-testid="field-next-day"
                >
                    <ChevronRight size={22} />
                </button>
            </div>

            {/* Job list */}
            <div className="px-4 py-4 space-y-3">
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-neutral-500">
                        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
                    </div>
                ) : todaysJobs.length === 0 ? (
                    <div
                        className="border-2 border-dashed border-neutral-300 py-16 text-center text-neutral-500"
                        data-testid="field-empty"
                    >
                        <CalendarIcon size={32} className="mx-auto mb-3" />
                        <p className="font-bold text-neutral-700">Nothing booked for {fmtHeader(day).toLowerCase()}.</p>
                        <p className="text-xs mt-1">Tap the calendar button below to jump to another day.</p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {todaysJobs.map((appt) => (
                            <SwipeableJobCard
                                key={appt.id}
                                appt={appt}
                                onDone={doDone}
                                onSkip={doSkip}
                                onOpen={setSelected}
                            />
                        ))}
                    </AnimatePresence>
                )}
            </div>

            {/* Bottom actions */}
            <div className="fixed bottom-4 left-4 right-4 flex items-center gap-2 justify-between">
                <button
                    onClick={() => setDay(new Date())}
                    className="dh-btn flex-1 bg-white"
                    data-testid="field-today-btn"
                >
                    Today
                </button>
                <button
                    onClick={openRoutePlan}
                    className="dh-btn flex-1 bg-white"
                    data-testid="field-route-btn"
                    disabled={todaysJobs.length === 0}
                >
                    <Navigation size={16} /> Route
                </button>
                <button
                    onClick={() => {
                        setMonthCursor(day);
                        setMonthOpen(true);
                    }}
                    className="dh-btn flex-1"
                    data-testid="field-month-btn"
                >
                    <CalendarIcon size={16} /> Month
                </button>
            </div>

            {/* Add-job FAB (floats above the bottom bar) */}
            <button
                onClick={openAddJob}
                className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-orange-600 hover:bg-orange-700 text-white shadow-xl border-2 border-neutral-900 flex items-center justify-center z-20"
                aria-label="Add job for this day"
                data-testid="field-add-job-fab"
            >
                <Plus size={26} />
            </button>

            {/* Month picker */}
            <MonthPicker
                open={monthOpen}
                onClose={() => setMonthOpen(false)}
                cursor={monthCursor}
                setCursor={setMonthCursor}
                appointmentsByDay={appointmentsByDay}
                onPick={(d) => {
                    setDay(d);
                    setMonthOpen(false);
                }}
            />

            {/* Tap-to-open job modal */}
            <JobModal
                appt={selected}
                onClose={() => setSelected(null)}
                onDone={doDone}
                onSkip={doSkip}
                onDelete={doDelete}
                onAddPart={addPart}
                onDeletePart={deletePart}
            />

            {/* Invoice dialog */}
            <Dialog open={!!doneAppt} onOpenChange={(o) => !o && setDoneAppt(null)}>
                <DialogContent className="bg-white border-2 border-neutral-900 rounded-none" data-testid="field-invoice-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase">
                            Send invoice
                        </DialogTitle>
                    </DialogHeader>
                    {doneAppt && (
                        <p className="text-sm text-neutral-600 -mt-2">
                            {doneAppt.customer_name} · {doneAppt.property_address}
                        </p>
                    )}
                    <form onSubmit={submitInvoice} className="space-y-3">
                        <div>
                            <label className="text-label block mb-1">Amount (£)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                required
                                placeholder="90.00"
                                inputMode="decimal"
                                className="dh-input text-xl font-bold"
                                data-testid="field-invoice-amount"
                                value={doneAmount}
                                onChange={(e) => setDoneAmount(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-label block mb-1">Description</label>
                            <input
                                className="dh-input"
                                data-testid="field-invoice-desc"
                                value={doneDesc}
                                onChange={(e) => setDoneDesc(e.target.value)}
                            />
                        </div>
                        <DialogFooter className="flex-wrap gap-2">
                            <button
                                type="button"
                                className="text-xs text-neutral-500 underline"
                                onClick={skipComplete}
                                disabled={doneSaving}
                                data-testid="field-invoice-skip"
                            >
                                Skip invoice, just complete
                            </button>
                            <button
                                className="dh-btn"
                                data-testid="field-invoice-send"
                                disabled={doneSaving}
                            >
                                {doneSaving ? "Sending…" : "Send invoice"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={!!deleteAppt} onOpenChange={(o) => !o && setDeleteAppt(null)}>
                <DialogContent
                    className="bg-white border-2 border-red-600 rounded-none max-w-md"
                    data-testid="field-delete-dialog"
                >
                    <div className="flex items-start gap-3 -mt-2">
                        <div className="shrink-0 w-11 h-11 bg-red-100 border-2 border-red-600 flex items-center justify-center">
                            <AlertTriangle size={22} className="text-red-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="font-display font-black text-xl uppercase leading-tight">
                                Delete this job?
                            </DialogTitle>
                            {deleteAppt && (
                                <p className="text-sm text-neutral-700 mt-2">
                                    <span className="font-bold">{deleteAppt.customer_name}</span>
                                    {" · "}
                                    <span className="text-neutral-500">{deleteAppt.property_address}</span>
                                </p>
                            )}
                            <p className="text-xs text-red-700 font-bold mt-3 uppercase tracking-wider">
                                This can&rsquo;t be undone. The job and any Google Calendar
                                event will be removed.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="flex-col-reverse sm:flex-row gap-2 mt-4">
                        <button
                            type="button"
                            className="dh-btn w-full sm:w-auto"
                            data-variant="ghost"
                            data-testid="field-delete-cancel"
                            onClick={() => setDeleteAppt(null)}
                            disabled={deleteSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="dh-btn w-full sm:w-auto !bg-red-600 hover:!bg-red-700 !border-red-800 !text-white"
                            data-testid="field-delete-confirm"
                            onClick={confirmDelete}
                            disabled={deleteSaving}
                        >
                            <Trash2 size={16} /> {deleteSaving ? "Deleting…" : "Delete job"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add-job (booking) dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent
                    className="bg-white border-2 border-neutral-900 rounded-none max-w-md max-h-[90vh] overflow-y-auto"
                    data-testid="field-add-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-xl uppercase">
                            Add job — {fmtDate(day)}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveAddJob} className="space-y-4 mt-2">
                        {/* Customer tabs */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                className={`dh-btn ${addMode === "existing" ? "" : "bg-white"}`}
                                onClick={() => setAddMode("existing")}
                                data-testid="field-add-mode-existing"
                            >
                                Existing
                            </button>
                            <button
                                type="button"
                                className={`dh-btn ${addMode === "new" ? "" : "bg-white"}`}
                                onClick={() => setAddMode("new")}
                                data-testid="field-add-mode-new"
                            >
                                New customer
                            </button>
                        </div>

                        {addMode === "existing" ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                        Customer
                                    </label>
                                    <Select
                                        value={addCustomerId}
                                        onValueChange={setAddCustomerId}
                                    >
                                        <SelectTrigger
                                            className="mt-1 border-2 border-neutral-900 rounded-none h-11"
                                            data-testid="field-add-customer"
                                        >
                                            <SelectValue placeholder="Pick customer…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {customers.map((c) => (
                                                <SelectItem
                                                    key={c.id}
                                                    value={c.id}
                                                    data-testid={`field-add-customer-${c.id}`}
                                                >
                                                    {c.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {customerProps.length > 1 && (
                                    <div>
                                        <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                            Property
                                        </label>
                                        <Select
                                            value={addPropertyId}
                                            onValueChange={setAddPropertyId}
                                        >
                                            <SelectTrigger
                                                className="mt-1 border-2 border-neutral-900 rounded-none h-11"
                                                data-testid="field-add-property"
                                            >
                                                <SelectValue placeholder="Pick property…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {customerProps.map((p) => (
                                                    <SelectItem
                                                        key={p.id}
                                                        value={p.id}
                                                    >
                                                        {p.address}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {addCustomerId && customerProps.length === 0 && (
                                    <div>
                                        <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                            New property address
                                        </label>
                                        <input
                                            type="text"
                                            value={addNewAddress}
                                            onChange={(e) => setAddNewAddress(e.target.value)}
                                            className="mt-1 w-full border-2 border-neutral-900 h-11 px-3"
                                            placeholder="1 Example Lane, Chichester"
                                            data-testid="field-add-property-new"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={addNewName}
                                    onChange={(e) => setAddNewName(e.target.value)}
                                    className="w-full border-2 border-neutral-900 h-11 px-3"
                                    placeholder="Customer name *"
                                    data-testid="field-add-new-name"
                                />
                                <input
                                    type="email"
                                    value={addNewEmail}
                                    onChange={(e) => setAddNewEmail(e.target.value)}
                                    className="w-full border-2 border-neutral-900 h-11 px-3"
                                    placeholder="Email (optional)"
                                    data-testid="field-add-new-email"
                                />
                                <input
                                    type="tel"
                                    value={addNewMobile}
                                    onChange={(e) => setAddNewMobile(e.target.value)}
                                    className="w-full border-2 border-neutral-900 h-11 px-3"
                                    placeholder="Mobile (optional)"
                                    data-testid="field-add-new-mobile"
                                />
                                <input
                                    type="text"
                                    value={addNewAddress}
                                    onChange={(e) => setAddNewAddress(e.target.value)}
                                    className="w-full border-2 border-neutral-900 h-11 px-3"
                                    placeholder="Property address *"
                                    data-testid="field-add-new-address"
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                    Service
                                </label>
                                <Select
                                    value={addServiceId}
                                    onValueChange={setAddServiceId}
                                >
                                    <SelectTrigger
                                        className="mt-1 border-2 border-neutral-900 rounded-none h-11"
                                        data-testid="field-add-service"
                                    >
                                        <SelectValue placeholder="Service…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {serviceTypes.map((s) => (
                                            <SelectItem
                                                key={s.id}
                                                value={s.id}
                                                data-testid={`field-add-service-${s.id}`}
                                            >
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                    Price (£)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={addPrice}
                                    onChange={(e) => setAddPrice(e.target.value)}
                                    className="mt-1 w-full border-2 border-neutral-900 h-11 px-3"
                                    placeholder="90.00"
                                    data-testid="field-add-price"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 items-end">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                    Time
                                </label>
                                <input
                                    type="time"
                                    value={addTime}
                                    onChange={(e) => {
                                        setAddTime(e.target.value);
                                        if (e.target.value) setAddTBC(false);
                                    }}
                                    disabled={addTBC}
                                    className="mt-1 w-full border-2 border-neutral-900 h-11 px-3 disabled:bg-neutral-100"
                                    data-testid="field-add-time"
                                />
                            </div>
                            <label className="flex items-center gap-2 h-11 pl-1 select-none">
                                <input
                                    type="checkbox"
                                    checked={addTBC}
                                    onChange={(e) => {
                                        setAddTBC(e.target.checked);
                                        if (e.target.checked) setAddTime("");
                                    }}
                                    className="w-5 h-5 border-2 border-neutral-900"
                                    data-testid="field-add-tbc"
                                />
                                <span className="text-sm font-bold uppercase tracking-wider">
                                    Time TBC
                                </span>
                            </label>
                        </div>

                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
                                Notes
                            </label>
                            <textarea
                                value={addNotes}
                                onChange={(e) => setAddNotes(e.target.value)}
                                rows={2}
                                className="mt-1 w-full border-2 border-neutral-900 p-2"
                                placeholder="Access notes, boiler model, gate code…"
                                data-testid="field-add-notes"
                            />
                        </div>

                        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                            <button
                                type="button"
                                className="dh-btn w-full sm:w-auto bg-white"
                                onClick={() => setAddOpen(false)}
                                disabled={addSaving}
                                data-testid="field-add-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="dh-btn w-full sm:w-auto"
                                disabled={addSaving}
                                data-testid="field-add-save"
                            >
                                {addSaving ? "Saving…" : "Book job"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Field;
